import './DroletCartItem.scss';
import '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Cart/CartItem.scss';

import React, { useCallback, useMemo } from 'react';
import {
    useRemoveItemFromCartAction,
    useUpdateCartItemsAction,
} from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/cart/actions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import LocalePrice from '@progymedia/gatsby-theme-boutik-ecommerce/src/utils/LocalePrice';
import QtyInput from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/QtyInput/QtyInput';
import GatsbyImageWrapper from '@progymedia/sbi-web-drolet/src/components/GatsbyImageWrapper';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { pushRemoveFromCartEvent } from '@progymedia/gatsby-theme-boutik-ecommerce/src/utils/useGTM_UA';
import { useAllProducts } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useAllProducts';
import { useAvailabilityInfo } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Cart/AddToCartButton';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useTranslation } from 'react-i18next';

interface ICartItem {
    item: any;
    setLoading: any;
    editable?: boolean;
    className?: string;
}

export default function CartItem({
    item,
    setLoading,
    editable = true,
    className = 'full',
}: ICartItem) {
    const { t } = useTranslation('layout');
    const [updateCartItems] = useUpdateCartItemsAction();
    const [removeCartItem] = useRemoveItemFromCartAction();
    const { currentLocale } = usePageLocalization();
    const [allProducts] = useAllProducts();
    const products = allProducts.nodes;
    const product = useMemo(
        () =>
            products.find((product: any) => product.code === item.product.sku),
        [item.product.sku, products]
    );

    const onItemRemoveClick = useCallback(
        async (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
            event.preventDefault();

            setLoading(true);
            pushRemoveFromCartEvent({
                id: item.product.sku,
                price: item.prices.price.value,
                currency: item.prices.price.currency,
                qty: item.quantity,
            });
            await removeCartItem(item.id);
            setLoading(false);
        },
        [item]
    );

    const updateQuantity = useCallback(
        async (newQuantity: any) => {
            try {
                setLoading(true);
                await updateCartItems(item.id, newQuantity);
                setLoading(false);
            } catch (error) {
                console.log(error);
            }
        },
        [item.id, item]
    );

    const { nextShipDate, availabilityLabel } = useAvailabilityInfo(product);

    return (
        <div className={`cartSingleItem ${className}`}>
            <div className='qItemImg'>
                <GatsbyImageWrapper fields={product.fields} />
            </div>

            <div className='qItemGroup'>
                <div className='qItemInfo'>
                    <p className='code'>{item.product.sku}</p>
                    <h4>{item.product.name}</h4>
                    <p className='singleItemPrice'>
                        {LocalePrice(currentLocale, item.prices.price.value)}
                    </p>
                    {availabilityLabel ===
                    t('layout:addToCartBtn.available') ? (
                        <span> {t('layout:addToCartBtn.available')}</span>
                    ) : (
                        <span className='availability'>
                            {t('addToCartBtn.dispo', {
                                date: nextShipDate,
                            })}
                        </span>
                    )}
                    <div
                        className='singleItemActions'
                        onClick={(e) => onItemRemoveClick(e)}
                    >
                        {t('delete')}
                    </div>
                </div>
            </div>

            <div className='qItemTotal'>
                {LocalePrice(currentLocale, item.prices.row_total.value)}
            </div>

            {editable && (
                <div className='qItemQty'>
                    <QtyInput
                        qtyValue={item.quantity}
                        setQtyValue={updateQuantity}
                    />
                </div>
            )}

            {editable && (
                <div className='qItemRemove'>
                    <span onClick={(e) => onItemRemoveClick(e)}>
                        <FontAwesomeIcon icon={faTimes} />
                    </span>
                </div>
            )}
        </div>
    );
}
